import {_} from 'lodash';
import * as ko from "knockout";
import {ICell, tpl as _tpl, viewModel as Search} from "../search/search"
import {
    IAuditLog,
    IErrorLog,
    IRouteDefinitionParams,
    ISearchAuditLogParams,
    ISecurityLog,
    ITableColumn,
    TBaseOrObservableOrFunctionType
} from "../../index";
import {getSearchParamsFn} from "../../util/request";
import {PermissionValidator} from "../../auth/PermissionValidator";
import {global} from "../../appContext";
import {storeObservable} from "../../util/sessionstore";
import {getHash} from "../../constants/RouteDefinitions";
import {getSearchMarkup} from "../../util/misc";
import {convertToServerString, getDateTime} from "../../util/datetime";
import {INPUT_DATE_FORMAT} from "../../constants";
import {moment} from 'moment';
import {memoryStore} from "../../util/memorystore";
import AbstractSearchHtml from './AbstractSearch.html?raw';
import AbstractSearchFilterHtml from './AbstractSearchFilter.html?raw';

export const tpl = _tpl
    .replace('<!-- {{FORM_INPUT}} -->', AbstractSearchHtml)
    .replace('<!-- {{FORM_INPUT_AFTER}} -->', AbstractSearchFilterHtml)

type TAll = IAuditLog | IErrorLog | ISecurityLog
type TDatePreset = '24h' | '7d' | '30d' | 'custom'

interface IDatePresetOption {
    key: Exclude<TDatePreset, 'custom'>;
    label: string;
}

const DEFAULT_DATE_PRESET: TDatePreset = '30d';

function defaultFromDate(): string {
    return moment().subtract(30, 'days').format(INPUT_DATE_FORMAT);
}

function defaultToDate(): string {
    return moment().format(INPUT_DATE_FORMAT);
}

export abstract class AbstractSearch<T = TAll> extends Search {
    protected abstract override readonly TABLE: ITableColumn<T>[]
    private pv: PermissionValidator = global.permissionValidator
    protected readonly allOrganizations: KnockoutObservable<boolean> = storeObservable('all.organizations')
    protected override readonly maxpagenr: TBaseOrObservableOrFunctionType<number> = 5000;
    private readonly maxdate: string = defaultToDate()
    private suppressDateSync = false

    private readonly datePreset: KnockoutObservable<TDatePreset>
    private readonly fromdate: KnockoutObservable<string>
    private readonly todate: KnockoutObservable<string>
    private readonly isDateMenuOpen: KnockoutObservable<boolean> = ko.observable(false)

    private readonly datePresetOptions: IDatePresetOption[] = [
        {key: '24h', label: 'search.filter.last24hours'},
        {key: '7d', label: 'search.filter.last7days'},
        {key: '30d', label: 'search.filter.last30days'},
    ]

    private readonly dateFilterLabel: KnockoutComputed<string> = ko.pureComputed(() => {
        const match = this.datePresetOptions.find((o) => o.key === this.datePreset())
        if (match) {
            return match.label
        }
        const formatDisplay = (raw: string): string => {
            const m = moment(raw, INPUT_DATE_FORMAT)
            return m.isValid() ? m.format('DD.MM.YYYY') : raw
        }
        const from = this.fromdate()
        const to = this.todate()
        if (from && to) {
            return `${formatDisplay(from)} – ${formatDisplay(to)}`
        }
        if (from || to) {
            return formatDisplay(from || to)
        }
        return 'search.filter.customrange'
    })

    /** Preset / empty custom = translation key; chosen custom range = formatted date. */
    private readonly dateFilterLabelIsKey: KnockoutComputed<boolean> = ko.pureComputed(() => {
        if (this.datePreset() !== 'custom') {
            return true
        }
        return !this.fromdate() && !this.todate()
    })

    protected constructor(params: IRouteDefinitionParams) {
        super(params);

        const filterKey = `log.${this.__routeId}.filter`
        this.datePreset = memoryStore.get(`${filterKey}.datepreset`, DEFAULT_DATE_PRESET)
        this.fromdate = memoryStore.get(`${filterKey}.fromdate`, defaultFromDate())
        this.todate = memoryStore.get(`${filterKey}.todate`, defaultToDate())

        // Migrate legacy generic search default (Name / ascending) to newest-first.
        if (this.sortcol() === 'Name') {
            this.sortcol('Registered')
            this.asc(false)
        }

        this.kosub.subscribe(this.fromdate, () => this.onCustomDateChanged())
        this.kosub.subscribe(this.todate, () => this.onCustomDateChanged())
        this.kosub.subscribe(this.allOrganizations, () => {
            this.preSubmit();
        });
    }

    protected init() {
        if (!this.pv.isMinOrganizationAdmin()) {
            const index = this.TABLE.findIndex(o => o.key == 'CompanyId')
            this.TABLE.splice(index, 1)
        }
        if (this.__routeParams?.['searchkey']) {
            this.searchkey(this.__routeParams['searchkey'])
            this.showAll(false)
        }
    }

    private boundsForPreset(preset: Exclude<TDatePreset, 'custom'>): {from: moment.Moment; to: moment.Moment} {
        const to = moment()
        switch (preset) {
            case '24h':
                return {from: moment().subtract(24, 'hours'), to}
            case '7d':
                return {from: moment().subtract(7, 'days').startOf('day'), to: to.clone().endOf('day')}
            case '30d':
            default:
                return {from: moment().subtract(30, 'days').startOf('day'), to: to.clone().endOf('day')}
        }
    }

    private getActiveDateBounds(): {from: moment.Moment | null; to: moment.Moment | null} {
        const preset = this.datePreset()
        if (preset !== 'custom') {
            return this.boundsForPreset(preset)
        }
        const fromRaw = this.fromdate()
        const toRaw = this.todate()
        return {
            from: fromRaw ? moment(fromRaw, INPUT_DATE_FORMAT).startOf('day') : null,
            to: toRaw ? moment(toRaw, INPUT_DATE_FORMAT).endOf('day') : null,
        }
    }

    private onCustomDateChanged() {
        if (this.suppressDateSync) {
            return
        }
        this.datePreset('custom')
        this.preSubmit()
    }

    private selectDatePreset(preset: Exclude<TDatePreset, 'custom'>, _: unknown, event: Event) {
        event.preventDefault()
        event.stopPropagation()
        this.suppressDateSync = true
        const bounds = this.boundsForPreset(preset)
        this.datePreset(preset)
        this.fromdate(bounds.from.format(INPUT_DATE_FORMAT))
        this.todate(bounds.to.format(INPUT_DATE_FORMAT))
        this.suppressDateSync = false
        this.isDateMenuOpen(false)
        this.preSubmit()
    }

    private onDateToggleClick(_: unknown, event: Event) {
        event.preventDefault()
        event.stopPropagation()
        this.isDateMenuOpen(!this.isDateMenuOpen())
    }

    private onDateFocusOut(_: unknown, event: FocusEvent) {
        const current = event.currentTarget as HTMLElement | null
        window.setTimeout(() => {
            if (!this.isDateMenuOpen()) {
                return
            }
            const active = document.activeElement
            if (current && active && current.contains(active)) {
                return
            }
            this.isDateMenuOpen(false)
        }, 0)
    }

    protected override getCellContent(obj: ICell<T>): string | number {
        let val = this.getCellVal(obj)
        switch ((obj.key as string).toLowerCase()) {
            case 'ip': {
                val = `<a href="https://whois.domaintools.com/${val}" target="_blank">${val}</a>`
                break
            }
            case 'callerid': {
                const o = obj.item as IErrorLog
                if (!val) {
                    return ''
                }
                val = `${this.markText(obj.key, val)}
                        <br>
                        <a href="${getHash('userdetails', {id: val}, true, o.companyid) ?? '#'}">${this.markText('callername' as keyof T, o.callername)}</a>`
                break;
            }
            case 'companyid': {
                const o = obj.item as TAll
                val = `${this.markText('companyid' as keyof T, val)}<br>${this.markText('companyname' as keyof T, o.companyname)}`
                break
            }
            case 'registered': {
                val = `${val}<br>${getDateTime(val, true)}`
                break;
            }
            case 'registeredby': {
                const o = obj.item as IAuditLog | ISecurityLog
                val = `${this.markText('registeredby' as keyof T, val)}
                <br>
                <a href="${getHash('userdetails', {id: val}, true, o.companyid) ?? '#'}">${this.markText('registeredbyname' as keyof T, o.registeredbyname)}</a>`
                break
            }
        }
        return super.getCellContent(_.defaultsDeep({val}, obj));
    }

    protected markText(key: keyof T, val: any): string {
        const str = String(val);
        if (!this.showAll() && this.isSearchableColumn(key) && this.searchkey()) {
            return getSearchMarkup(str, this.searchkey())
        }
        return str
    }

    protected override canRowClick(item: any): boolean {
        return false
    }

    protected override getParams(): ISearchAuditLogParams {
        const orgnr = this.allOrganizations() ? '' : global.login.selectedOrganizationId() ?? ''
        const {from: fromMoment, to: toMoment} = this.getActiveDateBounds()
        const fromdate: string = (fromMoment?.isValid() ? convertToServerString(fromMoment) : null) ?? ''
        const todate: string = (toMoment?.isValid() ? convertToServerString(toMoment) : null) ?? ''
        const serviceName = this.__routeDef.serviceName;
        const searchParamsFn = getSearchParamsFn(serviceName as Parameters<typeof getSearchParamsFn>[0]);
        return searchParamsFn({orgnr, fromdate, todate}, super.getParams()) as ISearchAuditLogParams;
    }

}
